/**
 * This component wraps React-Router's Link by providing name-based routing.
 *
 * The `name` prop should match a route in the flattened
 * routeConfiguration passed in context by the RoutesProvider
 * component. The `params` props is the route params for the route
 * path of the given route name.
 *
 * The `to` prop is an object with the same shape as Link requires,
 * but without `pathname` that will be generated from the given route
 * name.
 *
 * Some additional props can be passed for the <a> element like
 * `className` and `style`.
 *
 * The component can also be given the `activeClassName` prop that
 * will be added to the element className if the current URL matches
 * the one in the generated pathname of the link.
 */
import React, { useEffect } from 'react';
import { Link, withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { pathByRouteName, findRouteByRouteName } from '../../util/routes';

// Helper function to smoothly scroll to hash anchor
const scrollToHash = (hash, retries = 0) => {
  if (!hash) return;

  const id = hash.replace('#', '');
  const element = document.getElementById(id);

  if (element) {
    // Use setTimeout to ensure DOM is fully rendered
    setTimeout(() => {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 100);
  } else if (retries < 10) {
    // Retry if element not found (it might still be loading)
    setTimeout(() => scrollToHash(hash, retries + 1), 100);
  }
};

const smoothScrollToTop = () => {
  try {
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
  } catch (_) {}
  let start = typeof window !== 'undefined' ? window.pageYOffset : 0;
  let startTime = null;
  const duration = 300;
  const easeOutCubic = t => 1 - Math.pow(1 - t, 3);
  const step = ts => {
    if (startTime == null) startTime = ts;
    const progress = Math.min((ts - startTime) / duration, 1);
    const y = Math.round(start * (1 - easeOutCubic(progress)));
    if (typeof window !== 'undefined') window.scrollTo(0, y);
    if (progress < 1 && y > 0 && typeof window !== 'undefined') {
      window.requestAnimationFrame(step);
    }
  };
  if (typeof window !== 'undefined') window.requestAnimationFrame(step);
};

/**
 * This component wraps React-Router's Link by providing name-based routing.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.activeClassName class applied, when the link is the current link
 * @param {string?} props.name - name of the route in routeConfiguration
 * @param {Object?} props.params - path params for the named route and its pathname prop
 * @param {Object?} props.to - props for the React Router Link
 * @param {string?} props.to.search - search params for the React Router Link
 * @param {string?} props.to.hash - hash for the React Router Link
 * @param {Object?} props.to.state - state for the React Router Link (history.pushstate)
 * @param {any} props.children - the content of the link
 * @param {Object?} props.style - inline css for the link
 * @param {string?} props.title - title attribute for the 'a' element.
 * @param {Object?} props.match - match from React Router
 * @returns {JSX.Element} containing form that allows adding availability exceptions
 */
export const NamedLink = withRouter(props => {
  const routeConfiguration = useRouteConfiguration();
  const {
    name,
    params = {}, // pathParams
    title,
    // Link props
    to = {},
    children,
    match = {},
    // <a> element props
    className,
    style = {},
    activeClassName = 'NamedLink_active',
    onClick: onClickFromProps,
    onTouchEnd: onTouchEndFromProps,
  } = props;

  const onOver = () => {
    const { component: Page } = findRouteByRouteName(name, routeConfiguration);
    // Loadable Component has a "preload" function.
    if (Page.preload) {
      Page.preload();
    }
  };

  // Link props - define pathname early as it's used in handleClick
  const pathname = pathByRouteName(name, routeConfiguration, params);

  // Handle click for hash navigation
  const handleClick = (e) => {
    if (onClickFromProps) {
      onClickFromProps(e);
      if (e.defaultPrevented) {
        return;
      }
    }

    const { hash } = to;

    const currentPath = window.location.pathname;
    const targetPath = pathname;
    const normalizeSearch = s => (s ? (s.charAt(0) === '?' ? s : `?${s}`) : '');
    const targetSearch = normalizeSearch(to && to.search);
    const currentSearch = window.location.search || '';
    const currentHash = window.location.hash || '';

    if (hash) {
      const isSamePath = currentPath === targetPath;
      const isSameSearch = targetSearch === currentSearch;
      if (isSamePath && isSameSearch) {
        // Same location; just scroll to anchor
        e.preventDefault();
        scrollToHash(hash);
      } else {
        setTimeout(() => {
          scrollToHash(hash);
        }, 300);
      }
    } else if (currentPath === targetPath) {
      // Only intercept if search and hash are also identical (true same-location click)
      const isSameSearch = targetSearch === currentSearch;
      const isSameHash = !to?.hash || (to.hash === currentHash);
      if (isSameSearch && isSameHash) {
        e.preventDefault();
        smoothScrollToTop();
      }
    }
  };

  const active = match.url && match.url === pathname;

  // <a> element props
  const aElemProps = {
    className: classNames(className, { [activeClassName]: active }),
    style,
    title,
  };

  return (
    <Link
      onMouseOver={onOver}
      onTouchStart={onOver}
      onTouchEnd={onTouchEndFromProps}
      onClick={handleClick}
      to={{ pathname, ...to }}
      {...aElemProps}
    >
      {children}
    </Link>
  );
});

export default NamedLink;
